import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FiActivity,
  FiAlertTriangle,
  FiAward,
  FiCheckCircle,
  FiCoffee,
  FiShield,
  FiUsers,
} from "react-icons/fi";
import Layout from "../../components/Layout";
import TrainingHero from "./TrainingHero";
import ContactSection4 from "../contact/ContactSection4";

const SITE_URL = "https://mbasolutions.co";
const BANNER = `${process.env.PUBLIC_URL}/assets/training-banner.png`;

const whyChoosePoints = [
  "Internationally trusted Highfield qualifications that support career growth and workplace credibility",
  "Flexible delivery options for individuals and corporate teams, including classroom and blended formats",
  "Programs spanning Levels 1 to 4 — suitable for beginners through advanced learners",
  "Curriculum aligned with industry expectations for food safety, health & safety, and compliance",
];

const highfieldCourses = [
  {
    title: "Food Safety Courses",
    icon: FiCoffee,
    description:
      "Highfield Food Safety programs from foundation through advanced levels for catering, retail, and manufacturing teams. Learners cover safe food handling, hygiene, storage, and contamination control — helping organizations meet legal duties and protect consumers.",
    link: "/haccp-training",
    cta: "Explore Food Safety",
  },
  {
    title: "HACCP Training",
    icon: FiShield,
    description:
      "Structured Highfield-aligned HACCP learning focused on hazard identification, control measures, and Codex-based food safety management. Ideal for handlers, supervisors, and managers building stronger FSMS practices.",
    link: "/haccp-training",
    cta: "View HACCP Levels",
  },
  {
    title: "First Aid Courses",
    icon: FiActivity,
    description:
      "Practical emergency-response training covering essential first aid skills for workplaces. Participants build confidence to respond quickly and support a safer, better-prepared environment.",
    link: "/contact",
    cta: "Enquire Now",
  },
  {
    title: "Fire Safety Training",
    icon: FiAlertTriangle,
    description:
      "Learn fire prevention, hazard awareness, evacuation planning, and extinguisher use. These programs help offices, factories, and public facilities strengthen emergency readiness and compliance culture.",
    link: "/training",
    cta: "See Related Programs",
  },
  {
    title: "Health & Safety Courses",
    icon: FiAward,
    description:
      "Risk assessment, hazard identification, and accident-prevention practices designed for real workplace application. Completing Highfield H&S training supports safer operations and stronger team competence.",
    link: "/corporate-training",
    cta: "Learn More",
  },
];

const benefits = [
  "Earn internationally recognized Highfield qualifications",
  "Strengthen employability and professional progression",
  "Improve workplace safety knowledge and regulatory awareness",
  "Suitable for individual learners and corporate groups",
  "Delivered through structured classroom and flexible learning pathways",
];

const whoShouldEnroll = [
  "Employees in food, hospitality, retail, and manufacturing",
  "Supervisors and managers seeking certified competency",
  "Individuals pursuing internationally recognized Highfield awards",
  "Companies improving workforce safety, hygiene, and performance",
];

const enrollCourses = [
  "Highfield food safety courses",
  "Highfield HACCP training",
  "Highfield first aid courses",
  "Highfield fire safety training",
  "Highfield health & safety programs",
];

const relatedCourses = [
  { name: "Corporate Training", link: "/corporate-training" },
  { name: "ISO Lead Auditor Course", link: "/iso-lead-auditor-training" },
  { name: "HACCP Level 2 / 3 / 4", link: "/haccp-training" },
  { name: "PECB Training", link: "/pecb-training" },
  { name: "ISO Awareness Courses", link: "/iso-lead-auditor-training" },
  { name: "Internal Auditing Techniques", link: "/corporate-training" },
  { name: "All Training Programs", link: "/training" },
];

const SectionHeading = ({ title, subtitle }) => (
  <div className="text-center max-w-3xl mx-auto mb-12 md:mb-14">
    <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
      {title}
    </h2>
    <div
      className="w-20 h-1 bg-[#F86624] mx-auto rounded-full mb-5"
      aria-hidden="true"
    />
    {subtitle && (
      <p className="text-gray-600 text-base md:text-lg leading-relaxed">
        {subtitle}
      </p>
    )}
  </div>
);

const HighfieldTraining = () => {
  return (
    <Layout
      title="Highfield UK Training & Certification Courses | MBA Solutions"
      description="Enhance your career with Highfield UK training from MBA Solutions. Food safety, HACCP, first aid, fire safety, and health & safety courses with internationally recognized qualifications."
      keywords="Highfield UK training, Highfield certification courses, Highfield food safety, Highfield HACCP training, Highfield fire safety, Highfield first aid, health and safety courses, MBA Solutions Highfield"
      canonical={`${SITE_URL}/highfield-training`}
    >
      <TrainingHero
        badge="Highfield Training Partner"
        title="Highfield UK Training"
        subtitle="Enhance your career with Highfield UK training and certification courses — practical skills, internationally recognized qualifications, and flexible pathways for individuals and corporate teams."
        icon={FiAward}
        primaryCta={{ label: "Enquire About Courses", to: "/contact" }}
        secondaryCta={{ label: "All Training", to: "/training" }}
      />

      {/* Intro */}
      <section className="bg-white py-14 md:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
          >
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 leading-tight">
              Enhance Your Career with Highfield UK Training &amp; Certification
              Courses
            </h2>
            <div
              className="w-16 h-1 bg-[#F86624] rounded-full mb-6"
              aria-hidden="true"
            />
            <p className="text-gray-600 text-base md:text-lg leading-relaxed mb-4">
              Upgrade your skills with Highfield UK training delivered by MBA
              Solutions — a trusted pathway for professional certifications that
              help individuals and businesses build practical workplace
              capability.
            </p>
            <p className="text-gray-600 text-base md:text-lg leading-relaxed mb-4">
              Our programs focus on internationally recognized qualifications
              that support career progression and organizational compliance.
              Learners can enroll in Highfield courses covering food safety,
              HACCP, first aid, fire safety, and health &amp; safety.
            </p>
            <p className="text-gray-600 text-base md:text-lg leading-relaxed">
              Each course is designed for hands-on learning, clear assessment,
              and a globally recognized Highfield certification outcome that
              employers value.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
            className="relative"
          >
            <img
              src={BANNER}
              alt="Professionals completing Highfield UK training and certification"
              className="rounded-3xl shadow-2xl object-cover w-full h-[300px] md:h-[400px]"
              loading="lazy"
            />
            <div
              className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-[var(--bg-primary)]/25 to-[#F86624]/10"
              aria-hidden="true"
            />
          </motion.div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="bg-gray-50 py-14 md:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <SectionHeading
            title="Why Choose Highfield UK Training"
            subtitle="Highfield qualifications are recognized by organizations worldwide and provide a strong foundation for workplace skills and compliance."
          />
          <div className="max-w-4xl mx-auto bg-white rounded-2xl border border-gray-100 shadow-sm p-6 md:p-8">
            <ul className="space-y-4 list-none">
              {whyChoosePoints.map((point) => (
                <li
                  key={point}
                  className="flex items-start gap-3 text-gray-700"
                >
                  <FiCheckCircle
                    size={18}
                    className="text-[var(--bg-primary)] mt-0.5 flex-shrink-0"
                    aria-hidden="true"
                  />
                  <span className="text-sm md:text-base leading-relaxed">
                    {point}
                  </span>
                </li>
              ))}
            </ul>
            <p className="text-gray-600 text-sm md:text-base leading-relaxed mt-6 pt-5 border-t border-gray-100">
              Choosing Highfield UK training with MBA Solutions helps your skills
              align with current workplace expectations — and builds confidence
              to apply knowledge on the job.
            </p>
          </div>

          <div className="mt-12">
            <h3 className="text-center text-lg font-bold text-gray-900 mb-5">
              Related Courses
            </h3>
            <div className="flex flex-wrap justify-center gap-3">
              {relatedCourses.map((course) => (
                <Link
                  key={course.name}
                  to={course.link}
                  className="inline-flex items-center px-4 py-2.5 rounded-full border border-gray-200 bg-white text-sm font-medium text-gray-800 hover:border-[var(--bg-primary)]/40 hover:text-[var(--bg-primary)] transition-colors"
                >
                  {course.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Course cards */}
      <section className="bg-white py-14 md:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <SectionHeading
            title="Our Highfield Training Courses"
            subtitle="A wide variety of Highfield training courses for different industries and roles — combining theory with practical exercises, guided by experienced trainers."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {highfieldCourses.map((course, index) => {
              const Icon = course.icon;
              return (
                <motion.article
                  key={course.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: index * 0.05 }}
                  whileHover={{ y: -8 }}
                  className="group flex flex-col h-full bg-gray-50 hover:bg-white border border-gray-100 rounded-2xl p-6 md:p-7 shadow-sm hover:shadow-xl transition-all"
                >
                  <div className="w-12 h-12 rounded-xl bg-[var(--bg-primary)]/10 text-[var(--bg-primary)] flex items-center justify-center mb-5 group-hover:bg-[var(--bg-primary)] group-hover:text-white transition-colors">
                    <Icon size={22} aria-hidden="true" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-[var(--bg-primary)] transition-colors">
                    {course.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed flex-1 mb-6">
                    {course.description}
                  </p>
                  <Link
                    to={course.link}
                    className="inline-flex items-center text-sm font-semibold text-[var(--bg-primary)] hover:text-[#F86624] transition-colors"
                  >
                    {course.cta} →
                  </Link>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits + Who should enroll */}
      <section className="bg-gray-50 py-14 md:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl border border-gray-100 p-7 md:p-8 shadow-sm"
          >
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">
              Benefits of Highfield Certification
            </h2>
            <div className="w-16 h-1 bg-[#F86624] rounded-full mb-5" aria-hidden="true" />
            <ul className="space-y-3 list-none mb-5">
              {benefits.map((item) => (
                <li key={item} className="flex items-start gap-3 text-gray-700">
                  <FiCheckCircle
                    size={17}
                    className="text-[#F86624] mt-0.5 flex-shrink-0"
                    aria-hidden="true"
                  />
                  <span className="text-sm md:text-base leading-relaxed">
                    {item}
                  </span>
                </li>
              ))}
            </ul>
            <p className="text-sm text-gray-600 leading-relaxed">
              A Highfield certification course demonstrates commitment to
              professional development — and helps organizations show that
              teams are trained to recognized standards.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.08 }}
            className="bg-white rounded-2xl border border-gray-100 p-7 md:p-8 shadow-sm"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-11 h-11 rounded-xl bg-[var(--bg-primary)]/10 text-[var(--bg-primary)] flex items-center justify-center">
                <FiUsers size={20} aria-hidden="true" />
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900">
                Who Should Enroll?
              </h2>
            </div>
            <div className="w-16 h-1 bg-[#F86624] rounded-full mb-5" aria-hidden="true" />
            <ul className="space-y-3 list-none mb-5">
              {whoShouldEnroll.map((item) => (
                <li key={item} className="flex items-start gap-3 text-gray-700">
                  <span
                    className="w-1.5 h-1.5 rounded-full bg-[var(--bg-primary)] mt-2 flex-shrink-0"
                    aria-hidden="true"
                  />
                  <span className="text-sm md:text-base leading-relaxed">
                    {item}
                  </span>
                </li>
              ))}
            </ul>
            <p className="text-sm text-gray-600 leading-relaxed">
              Participants gain practical knowledge and the confidence to apply
              skills on the job — preparing them for real workplace challenges.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Why training center + How to enroll */}
      <section className="bg-white py-14 md:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-start">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Why Train with MBA Solutions?
            </h2>
            <div
              className="w-16 h-1 bg-[#F86624] rounded-full mb-6"
              aria-hidden="true"
            />
            <p className="text-gray-600 text-base md:text-lg leading-relaxed mb-4">
              MBA Solutions provides structured, supportive Highfield training
              pathways with experienced instructors who guide learners through
              each stage of the program.
            </p>
            <p className="text-gray-600 text-base md:text-lg leading-relaxed">
              Enrollment is straightforward for individuals and corporate
              groups. You gain hands-on practice, clear learning outcomes, and
              support toward successful course completion.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-gray-50 rounded-2xl border border-gray-100 p-7 md:p-8"
          >
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">
              How to Enroll
            </h2>
            <div className="w-16 h-1 bg-[#F86624] rounded-full mb-5" aria-hidden="true" />
            <p className="text-gray-600 text-sm md:text-base leading-relaxed mb-5">
              Start your Highfield UK training journey with MBA Solutions.
              Popular pathways include:
            </p>
            <ul className="space-y-3 list-none mb-6">
              {enrollCourses.map((item) => (
                <li key={item} className="flex items-center gap-3 text-gray-800">
                  <FiCheckCircle
                    size={16}
                    className="text-[var(--bg-primary)] flex-shrink-0"
                    aria-hidden="true"
                  />
                  <span className="text-sm md:text-base font-medium">
                    {item}
                  </span>
                </li>
              ))}
            </ul>
            <p className="text-sm text-gray-600 mb-5">
              Contact us for schedules, fees, and registration details — and
              gain internationally recognized Highfield qualifications.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center px-6 py-3 rounded-full bg-[var(--bg-primary)] hover:opacity-90 text-white font-semibold shadow-md transition-all"
            >
              Request Schedule &amp; Fees
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Conclusion */}
      <section className="bg-gray-50 py-12 md:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
            Conclusion
          </h2>
          <div
            className="w-16 h-1 bg-[#F86624] mx-auto rounded-full mb-6"
            aria-hidden="true"
          />
          <p className="text-gray-600 text-base md:text-lg leading-relaxed">
            Improve your skills and career prospects with Highfield UK training
            from MBA Solutions. Our Highfield certification courses deliver
            practical knowledge, international recognition, and workplace
            confidence — whether you learn in a facilitated setting or through
            flexible blended formats. Invest in your future by enrolling today.
          </p>
        </div>
      </section>

      {/* Contact — Certifications enquiry form */}
      <section
        className="bg-white pt-10 md:pt-14"
        aria-label="Contact for training courses"
      >
        <div className="max-w-3xl mx-auto px-4 text-center mb-2">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
            Contact for Training Courses
          </h2>
          <div
            className="w-20 h-1 bg-[#F86624] mx-auto rounded-full mb-4"
            aria-hidden="true"
          />
          <p className="text-gray-600">
            Ask about Highfield UK training schedules, fees, and group bookings
            — our team will respond shortly.
          </p>
        </div>
        <ContactSection4 />
      </section>

      {/* Closing CTA */}
      <section className="relative overflow-hidden py-14 md:py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#1a1a2e] via-[#3d1f5c] to-[var(--bg-primary)] text-white">
        <div className="relative max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Start Your Highfield UK Training Today
          </h2>
          <div className="w-20 h-1 bg-[#F86624] mx-auto rounded-full mb-5" aria-hidden="true" />
          <p className="text-white/85 mb-8 leading-relaxed">
            Gain internationally recognized certification and hands-on skills
            with expert-led Highfield training courses. Flexible programs for
            individuals and corporate teams help you meet professional goals
            with confidence.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[#F86624] hover:bg-[#e55a1a] text-white font-semibold shadow-lg transition-all"
          >
            Book Highfield Training
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default HighfieldTraining;
